var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Auth0Client } from '@auth0/auth0-spa-js';
import { oauthToken } from '@auth0/auth0-spa-js/src/api';
import { GenericError } from '@auth0/auth0-spa-js/src/errors';
import { bufferToBase64UrlEncoded, createRandomString, encode, runIframe, sha256 } from '@auth0/auth0-spa-js/src/utils';
// Typescript not compliant parts in this class
// Do not take this as example
// There is not simple way how to override unwanted behaviour in `Auth0 client`
// @ts-ignore: Overriding private methods
class OpenIdConnectClient extends Auth0Client {
    constructor(options, namespacePrefix, onlyInternalFlow = false) {
        super(options);
        // for some reason `Auth0` client doesn't filter this prior authorize url creation
        // used for extra filtering just to not to send unnecessary data
        this.allowedAuthorizeOptions = [
            'response_type',
            'response_mode',
            'code_challenge',
            'code_challenge_method',
            'client_id',
            'redirect_uri',
            'scope',
            'audience',
            'origin',
            'state',
            'screen_hint',
            'nonce',
            'prompt'
        ];
        this.namespacePrefix = namespacePrefix;
        this.onlyInternalFlow = onlyInternalFlow;
        this.overridePrivateProperties(options);
    }
    getAccessToken(scope, audience, clientId) {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield this.getCacheEntry(scope, audience, clientId)).access_token;
        });
    }
    getIdToken(scope, audience, clientId) {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield this.getCacheEntry(scope, audience, clientId)).id_token;
        });
    }
    getCacheEntry(scope, audience, clientId) {
        return __awaiter(this, void 0, void 0, function* () {
            // @ts-ignore: Call private method from super class
            const entry = yield this._getEntryFromCache({
                scope: scope,
                audience: audience,
                client_id: clientId,
                getDetailedEntry: true
            });
            if (!entry) {
                return {};
            }
            return entry;
        });
    }
    // override private method which is creating authorization url
    _authorizeUrl(authorizeOptions) {
        // filter just allowed authorize params
        const params = Object.assign({}, ...this.allowedAuthorizeOptions.map(key => {
            const val = authorizeOptions[key];
            return val ? { [key]: val } : {};
        }));
        if (this.onlyInternalFlow) {
            // param naming is ambiguous - enforces internal flow authentication (inker)
            params['origin'] = 'internal';
        }
        // @ts-ignore: Call private method from super class
        const url = super._authorizeUrl(params);
        // create proper URL for profiles `authorize`
        // remove `auth0Client version` identification
        // @ts-ignore: Access private property
        return url.replace(this.domainUrl, `${this.domainUrl}/oauth`)
            .replace(/&auth0Client=[^=&=]+/, '');
    }
    // Seems like best workaround how to extend Auth0 client
    overridePrivateProperties(options) {
        // Override property init in super class
        // enable `http` based JWT issuer
        // @ts-ignore: Private property of super class
        this.domainUrl = options.domain;
        // @ts-ignore: Private property of super class
        this.tokenIssuer = options.issuer;
        const tokenUrl = new URL(options.issuer);
        this.tokenOrigin = tokenUrl.origin;
        // @ts-ignore: Private property of super class
        this.isAuthenticatedCookieName = `${this.namespacePrefix}.${options.client_id}.is.authenticated`;
    }
    // override of private method for iframe token silent renew
    // issue with `web_message` (post message) origin
    // conflict how profiles is served with `/profiles` slug
    // lot of not good typescript practices - DISCLAIMER: please don't look for inspiration in this method
    // @ts-ignore: Override private method
    _getTokenFromIFrame(options) {
        return __awaiter(this, void 0, void 0, function* () {
            const stateIn = encode(createRandomString());
            const nonceIn = encode(createRandomString());
            const code_verifier = createRandomString();
            const code_challengeBuffer = yield sha256(code_verifier);
            const code_challenge = bufferToBase64UrlEncoded(code_challengeBuffer);
            // @ts-ignore: Invoke private method
            const params = this._getParams(options, stateIn, nonceIn, code_challenge, options.redirect_uri ||
                // @ts-ignore: Access private property
                this.options.redirect_uri ||
                window.location.origin);
            const url = this._authorizeUrl(Object.assign(Object.assign({}, params), { prompt: 'none', response_mode: 'web_message' }));
            // @ts-ignore: Access private property
            const timeout = options.timeoutInSeconds || this.options.authorizeTimeoutInSeconds;
            try {
                // When a browser is running in a Cross-Origin Isolated context, using iframes is not possible.
                // It doesn't throw an error but times out instead, so we should exit early and inform the user about the reason.
                // https://developer.mozilla.org/en-US/docs/Web/API/crossOriginIsolated
                if (window.crossOriginIsolated) {
                    throw new GenericError('login_required', 'The application is running in a Cross-Origin Isolated context, silently retrieving a token without refresh token is not possible.');
                }
                // @ts-ignore: Access private property
                const codeResult = yield runIframe(url, this.tokenOrigin, timeout);
                if (stateIn !== codeResult.state) {
                    throw new Error('Invalid state');
                }
                const { scope, audience, redirect_uri, ignoreCache, timeoutInSeconds } = options, customOptions = __rest(options, ["scope", "audience", "redirect_uri", "ignoreCache", "timeoutInSeconds"]);
                const tokenResult = yield oauthToken(Object.assign(Object.assign(Object.assign({}, this.customOptions), customOptions), { scope,
                    audience, 
                    // @ts-ignore: Access private property
                    baseUrl: this.domainUrl, 
                    // @ts-ignore: Access private property
                    client_id: this.options.client_id, code_verifier, code: codeResult.code, grant_type: 'authorization_code', redirect_uri: params.redirect_uri, 
                    // @ts-ignore: Access private property
                    auth0Client: this.options.auth0Client, 
                    // @ts-ignore: Access private property
                    useFormData: this.options.useFormData }), 
                // @ts-ignore: Access private property
                this.worker);
                // @ts-ignore: Access private property
                const decodedToken = yield this._verifyIdToken(tokenResult.id_token, nonceIn);
                return Object.assign(Object.assign({}, tokenResult), { decodedToken, scope: params.scope, oauthTokenScope: tokenResult.scope, audience: params.audience || 'default' });
            }
            catch (e) {
                if (e.message === 'login_required') {
                    this.logout({
                        localOnly: true
                    });
                }
                throw e;
            }
        });
    }
}
export default OpenIdConnectClient;
//# sourceMappingURL=OpenIdConnectClient.js.map